import { useContext, useState } from 'react'
import { Box } from '@mui/material'
import { useToast } from '../../../../contexts/toast.context'
import TableComponent from '../../../shared/Table'
import PlatformModal from '../../Modals/PlatformModal'
import { IPlatform } from '../../../../types/spectrum-manager.type'
import { PlatformsConfigs } from '../../../../utils/constants'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'
import { createPlatform, deletePlatform, updatePlatform } from '../../../../services/spectrum-manager.service'
import { AppConfigContext } from '../../../../contexts/app-config.context'
import { toNumberOrNull } from '../../../../utils/functions'
import { usePermission } from '../../../../hooks/usePermission'

import styles from './styles.module.scss'

// Build a payload with the user-typed string values converted to numbers for
// the columns the backend stores numerically.
const toPayload = (p: IPlatform): IPlatform => ({
  ...p,
  max_altitude: toNumberOrNull(p.max_altitude) as number,
})

export const Platforms = () => {
  const [isCreating, setIsCreating] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [platform, setPlatform] = useState<IPlatform | null>(null)

  const { showToast } = useToast()
  const { isLightMode } = useContext(AppConfigContext)
  const { platforms, setPlatforms } = useContext(SpectrumManagerContext)
  const hasWriteAccess = usePermission('write', 'platforms')

  const handleRemovePlatform = async (id: number) => {
    const data = await deletePlatform(id)

    if (data.success) {
      setPlatforms(platforms.filter((platform) => platform.id !== id))
      showToast('success', 'Platform is deleted successfully')
    }
  }

  const handleCreatePlatform = async () => {
    if (platform) {
      const data = await createPlatform(toPayload(platform))

      if (data.data) {
        setPlatforms([data.data, ...platforms])
        showToast('success', 'Platform is created successfully')
        setIsCreating(false)
        setPlatform(null)
      }
    }
  }

  const handleSave = async () => {
    if (isCreating) {
      handleCreatePlatform()
      return
    }

    if (platform) {
      const data = await updatePlatform(platform.id, toPayload(platform))

      if (data.data) {
        setPlatforms((prev) =>
          prev.map((platform) => (data.data && platform.id === data.data.id ? data.data : platform)),
        )
        showToast('success', 'Platform is updated successfully')
        setPlatform(null)
        setIsEditing(false)
      }
    }
  }

  const onPlatformCreate = () => {
    setIsCreating(true)
    setPlatform({} as IPlatform)
  }

  const handleChange = async (key: string, value: string) => {
    const changedPlatform = platform ? platform : ({} as IPlatform)

    setPlatform({ ...changedPlatform, [key]: value })
  }

  return (
    <Box className={styles.platforms}>
      <TableComponent
        isLightMode={isLightMode}
        data={platforms}
        configs={PlatformsConfigs}
        handleRefetch={() => {}}
        handleCreate={onPlatformCreate}
        hasWriteAccess={!!hasWriteAccess}
        handleView={(item: IPlatform) => {
          setPlatform(item)
          setIsEditing(true)
        }}
        handleDelete={handleRemovePlatform}
      />
      {platform && (
        <PlatformModal
          platform={platform}
          isLightMode={isLightMode}
          open={isCreating || isEditing}
          handleSave={handleSave}
          fields={PlatformsConfigs.items}
          onClose={() => {
            setIsCreating(false)
            setIsEditing(false)
          }}
          handleChange={handleChange}
        />
      )}
    </Box>
  )
}
